"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const common_utils_1 = require("@zapier/common-utils");
const react_1 = require("react");
const design_system_1 = require("@zapier/design-system");
const theme_1 = require("@zapier/theme");
const theme_2 = require("../../theme");
const common_components_1 = require("@zapier/common-components");
const StepMenuButton_1 = __importDefault(require("../StepMenuButton"));
const menuBorderRadius = '4px';
const menuContainerCss = {
    display: 'inline-block',
    position: 'relative',
};
const menuCss = {
    background: design_system_1.Colors.white,
    borderRadius: menuBorderRadius,
    border: theme_2.Styles.DefaultBorder,
    boxShadow: `0 2px 4px rgba(0,0,0, 0.2)`,
    color: design_system_1.Colors.grey500,
    fontSize: '14px',
    lineHeight: '1.75em',
    margin: '0 0 20px',
    position: 'absolute',
    right: '0px',
    textAlign: 'left',
    top: '35px',
    width: '180px',
    zIndex: 10,
};
const menuItemCss = {
    border: `2px solid ${design_system_1.Colors.transparent}`,
    color: design_system_1.Colors.grey700,
    cursor: 'pointer',
    fontWeight: 600,
    padding: '10px 20px',
    width: '100%',
    '&:hover': {
        background: design_system_1.Colors.grey100,
    },
    '&:focus': {
        border: `2px solid ${design_system_1.Colors.blue500}`,
        color: design_system_1.Colors.blue500,
    },
    '&:first-of-type': {
        borderTopLeftRadius: menuBorderRadius,
        borderTopRightRadius: menuBorderRadius,
    },
    '&:last-child': {
        borderBottomLeftRadius: menuBorderRadius,
        borderBottomRightRadius: menuBorderRadius,
    },
};
const getDeleteMenuItemCss = ({ isConfirming }) => (Object.assign(Object.assign({}, menuItemCss), (isConfirming
    ? {
        color: design_system_1.Colors.red500,
    }
    : {})));
const useForceFocus = (shouldFocus) => {
    const itemRef = react_1.useRef(null);
    react_1.useEffect(() => {
        if (itemRef.current && shouldFocus) {
            itemRef.current.focus();
        }
    }, [shouldFocus]);
    return itemRef;
};
const RenameMenuItem = ({ onRename, onFocus, onKeyDown, shouldFocus, isInPath, }) => {
    const itemRef = useForceFocus(shouldFocus);
    return (common_utils_1.isolateComponent("button", { css: menuItemCss, onClick: onRename, onFocus: onFocus, onKeyDown: onKeyDown, ref: itemRef },
        "Rename ",
        isInPath ? 'path' : 'step',
        "\u2026"));
};
const DeleteMenuItem = ({ onDelete, onFocus, onKeyDown, shouldFocus, isInPath, }) => {
    const [isConfirming, setIsConfirming] = react_1.useState(false);
    const itemRef = useForceFocus(shouldFocus);
    const onClick = () => {
        if (isConfirming) {
            onDelete();
        }
        else {
            setIsConfirming(true);
        }
    };
    const noun = isInPath ? 'path' : 'step';
    return (common_utils_1.isolateComponent(common_components_1.Timeout, { delay: theme_1.Duration.confirmationTimeout, isWaiting: isConfirming, onTimeout: () => {
            setIsConfirming(false);
        } },
        common_utils_1.isolateComponent("button", { css: getDeleteMenuItemCss({
                isConfirming,
            }), onClick: onClick, onFocus: onFocus, onKeyDown: onKeyDown, ref: itemRef }, isConfirming ? `Really delete ${noun}?` : `Delete ${noun}…`)));
};
const StepMenu = ({ hasDelete, hasRename, onDelete, onRename, isInPath, }) => {
    const buttonRef = react_1.useRef(null);
    const menuRef = react_1.useRef(null);
    const [isMenuOpen, setIsMenuOpen] = react_1.useState(false);
    const [focusedIndex, setFocusedIndex] = react_1.useState(-1);
    const createItemFocusHandler = (focusIndex) => () => setFocusedIndex(focusIndex);
    const onOpenMenu = () => {
        setIsMenuOpen(true);
        setFocusedIndex(-1);
    };
    const onCloseMenu = () => {
        setFocusedIndex(-1);
        setIsMenuOpen(false);
    };
    const handleToggleKeyDown = common_utils_1.createKeyHandler(['down', 'escape'], e => {
        e.preventDefault();
        e.stopPropagation();
        if (e.key === 'ArrowDown') {
            onOpenMenu();
            setFocusedIndex(hasRename ? 0 : 1);
        }
        if (e.key === 'Escape') {
            onCloseMenu();
        }
    });
    const handleItemKeyDown = common_utils_1.createKeyHandler(['up', 'down', 'escape'], e => {
        e.preventDefault();
        e.stopPropagation();
        if (e.key === 'Escape') {
            if (buttonRef.current) {
                buttonRef.current.focus();
            }
            onCloseMenu();
        }
        else if (e.key === 'ArrowUp') {
            setFocusedIndex(focusedIndex === 0 ? 1 : 0);
        }
        else if (e.key === 'ArrowDown') {
            setFocusedIndex(focusedIndex === 0 ? 1 : 0);
        }
    });
    const handleRename = () => {
        onCloseMenu();
        onRename();
    };
    const handleDelete = () => {
        onCloseMenu();
        onDelete();
    };
    const handleBlur = (e) => {
        if (menuRef.current && !menuRef.current.contains(e.relatedTarget)) {
            setFocusedIndex(-1);
        }
    };
    return (common_utils_1.isolateComponent("div", { css: menuContainerCss },
        common_utils_1.isolateComponent(StepMenuButton_1.default, { ariaLabel: "Step menu", buttonRef: buttonRef, iconName: "more", isActive: isMenuOpen, onClick: onOpenMenu, onKeyDown: handleToggleKeyDown }),
        common_utils_1.isolateComponent(common_components_1.OutsideClickable, { onClickOutside: onCloseMenu }, isMenuOpen && (common_utils_1.isolateComponent("div", { "aria-label": "menu", css: menuCss, onBlur: handleBlur, ref: menuRef, role: "menu" },
            hasRename && (common_utils_1.isolateComponent(RenameMenuItem, { isInPath: isInPath, onFocus: createItemFocusHandler(0), onKeyDown: handleItemKeyDown, onRename: handleRename, shouldFocus: focusedIndex === 0 })),
            hasDelete && (common_utils_1.isolateComponent(DeleteMenuItem, { isInPath: isInPath, onDelete: handleDelete, onFocus: createItemFocusHandler(1), onKeyDown: handleItemKeyDown, shouldFocus: focusedIndex === 1 })))))));
};
const FastStepMenu = react_1.memo(StepMenu);
exports.default = FastStepMenu;
